import { treaty } from "@elysiajs/eden";
import type { App } from "../api";
import { useEffect, useState, useCallback } from "react";
import { blobUrlToFile } from "./utils";
import type { OutputBlockData } from "@editorjs/editorjs";

type EdenOptions<T> = {
  onSuccess?: (data: T) => void;
  onError?: (error: unknown) => void;
  onSettled?: () => void;
};

type EdenResult<T> = {
  data: T | null;
  error: unknown;
  loading: boolean;
  refetch?: () => void;
};

export function useEden<T>(fn: () => Promise<{ data: T }>, options?: EdenOptions<T>): EdenResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [refetchKey, setRefetchKey] = useState(0);

  const refetch = useCallback(() => {
    setRefetchKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    fn()
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          options?.onSuccess?.(res.data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          options?.onError?.(err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          options?.onSettled?.();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [refetchKey]);

  return { data, error, loading, refetch };
}

export const client = treaty<App>(window.location.origin);

// TODO fix types in here
export const useBlogsWithPosts = (options?: EdenOptions<any>) => useEden(client.api.blogs.get, options);

export const usePostById = (id: string, options?: EdenOptions<any>) => useEden(client.api.posts({ id }).get, options);

export const useBlogMeta = (blog: string, options?: EdenOptions<any>) => useEden(() => client.api["blog-meta"].get({ query: { blog } }), options);

// The post body's `content` is a jsonb column, so Eden widens it to `File | File[]`.
// Restore the real EditorJS block type here and cast at the call sites.
type EdenNewPost = Parameters<typeof client.api.blogposts.post>[0];
export type NewPost = Omit<EdenNewPost, "content"> & { content: OutputBlockData[] };
export const createPost = (body: NewPost) => client.api.blogposts.post(body as unknown as EdenNewPost);

export type LocalizationInfo = Parameters<typeof client.api.localization.post>[0];
export const localizePost = (body: LocalizationInfo) => client.api.localization.post(body);

type EdenPatchPost = Parameters<ReturnType<typeof client.api.posts>["patch"]>[0];
export const updatePost = (id: string, body: Partial<NewPost>) => client.api.posts({ id }).patch(body as unknown as EdenPatchPost);

export const deletePost = (id: string) => client.api.posts({ id }).delete();

export type NewCategory = Parameters<typeof client.api.category.post>[0];
export const createCategory = (body: NewCategory) => client.api.category.post(body);

export const deployBlog = (id: number | string) => client.api.posts.deploy.patch({ postId: String(id) });

export const uploadImage = (image: File) => client.api.image.post({ image });

export const uploadImgByUrl = (imageUrl: string) => client.api["upload-image"].post({ url: imageUrl });

export const uploadAndNormalizeUrl = async (url?: string) => {
  if (!url) return url;

  if (url.startsWith(window.location.origin)) {
    return url; // already uploaded
  }

  if (url.startsWith("blob:")) {
    const file = await blobUrlToFile(url, "image.png");
    const res = await uploadImage(file);
    return `${window.location.origin}/${res.data?.path}`;
  }

  const res = await uploadImgByUrl(url);
  return `${window.location.origin}/${res.data?.path}`;
};
